import { useState } from 'react';
import type { FormEvent } from 'react';
import { esquemaCambiarContrasena } from '@copathy/shared';
import { ErrorApi } from '../lib/api';
import { useCambiarContrasena, useMe } from '../lib/auth';
import { Boton } from '../componentes/Boton';
import { Campo } from '../componentes/Campo';
import { Candado, Escudo, Ojo, OjoTachado } from '../componentes/iconos';

/**
 * Mi perfil: los datos de la propia cuenta y el cambio de contraseña.
 *
 * ── Lo que esta pantalla NO hace ──
 * No edita el nombre ni el correo. El correo es la identidad con la que se entra
 * y cambiarlo es una operación con su propia verificación; el nombre lo administra
 * quien gestiona usuarios. Mostrarlos como campos deshabilitados sería prometer
 * algo que no existe, así que van como texto.
 */
export function Perfil() {
  const { usuario } = useMe();
  const cambiar = useCambiarContrasena();

  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [ver, setVer] = useState(false);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);
  const [listo, setListo] = useState(false);
  const [enviando, setEnviando] = useState(false);

  if (!usuario) return null;

  async function alEnviar(e: FormEvent) {
    e.preventDefault();
    setErrores({});
    setErrorGeneral(null);
    setListo(false);

    /*
      La repetición se comprueba SOLO acá. No va en el esquema compartido porque
      el servidor no la recibe: mandarle dos veces la misma contraseña le daría
      un dato que no necesita para nada, y un campo más que registrar por error.
      Es una ayuda contra el dedo torpe, no una regla del contrato.
    */
    if (nueva !== repetida) {
      setErrores({ repetida: 'Las dos no coinciden.' });
      return;
    }

    // El MISMO esquema Zod que valida el servidor.
    const r = esquemaCambiarContrasena.safeParse({
      contrasenaActual: actual,
      contrasenaNueva: nueva,
    });
    if (!r.success) {
      const campos: Record<string, string> = {};
      for (const issue of r.error.issues) {
        const clave = String(issue.path[0] ?? '_');
        campos[clave] ??= issue.message;
      }
      setErrores(campos);
      return;
    }

    setEnviando(true);
    try {
      await cambiar(r.data.contrasenaActual, r.data.contrasenaNueva);
      setActual('');
      setNueva('');
      setRepetida('');
      setListo(true);
    } catch (err) {
      if (err instanceof ErrorApi) {
        if (err.campos) setErrores(err.campos);
        else setErrorGeneral(err.message);
      } else {
        setErrorGeneral('No pudimos conectar con el servidor. Revisa tu conexión.');
      }
    } finally {
      setEnviando(false);
    }
  }

  const ojo = (
    <button
      type="button"
      onClick={() => setVer((v) => !v)}
      title={ver ? 'Ocultar' : 'Mostrar'}
      aria-label={ver ? 'Ocultar contraseñas' : 'Ver contraseñas'}
      className="rounded-full p-2.5 text-marino-400 transition hover:bg-marino-50 hover:text-marino-700"
    >
      {ver ? <OjoTachado size={18} /> : <Ojo size={18} />}
    </button>
  );

  return (
    <div className="max-w-2xl space-y-6">
      <section className="rounded-[var(--radius-tarjeta)] border border-marino-100 bg-white p-6">
        <h2 className="font-semibold text-marino-800">Tu cuenta</h2>
        <dl className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-semibold tracking-wide text-marino-400 uppercase">
              Nombre
            </dt>
            <dd className="mt-0.5 text-marino-800">{usuario.nombre}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold tracking-wide text-marino-400 uppercase">
              Correo
            </dt>
            <dd className="mt-0.5 break-all text-marino-800">{usuario.correo}</dd>
          </div>
          <div>
            {/*
              «Organización» y no «Cliente»: para el personal de Copathy esta es
              su propia casa, no un cliente. Y «Empresa» no se dice en pantalla
              (D-035), aunque en la base la columna se llame así.
            */}
            <dt className="text-xs font-semibold tracking-wide text-marino-400 uppercase">
              Organización
            </dt>
            <dd className="mt-0.5 text-marino-800">{usuario.empresaNombre}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold tracking-wide text-marino-400 uppercase">Rol</dt>
            <dd className="mt-0.5 text-marino-800">{usuario.rol}</dd>
          </div>
        </dl>
        <p className="mt-4 text-sm text-marino-400">
          El nombre y el correo los administra COPATHY. Si hay que corregir alguno, pídelo.
        </p>
      </section>

      <section className="rounded-[var(--radius-tarjeta)] border border-marino-100 bg-white p-6">
        <h2 className="font-semibold text-marino-800">Cambiar la contraseña</h2>

        {/*
          El aviso va ANTES del formulario, no después de enviarlo: que se cierren
          las demás sesiones es una consecuencia que conviene saber antes de
          decidir, sobre todo para quien tenga la aplicación abierta en el teléfono.
        */}
        <p className="mt-2 flex items-start gap-2 text-sm text-marino-500">
          <span className="mt-0.5 shrink-0 text-naranja-600">
            <Escudo size={16} />
          </span>
          <span>
            Al cambiarla se cierran <strong className="font-semibold">todas</strong> tus otras
            sesiones, incluida la de la aplicación en el teléfono. En esta pantalla sigues dentro.
          </span>
        </p>

        <form onSubmit={alEnviar} noValidate className="mt-5 space-y-4">
          <Campo
            etiqueta="Contraseña actual"
            type={ver ? 'text' : 'password'}
            autoComplete="current-password"
            icono={<Candado size={18} />}
            value={actual}
            onChange={(e) => setActual(e.target.value)}
            error={errores.contrasenaActual}
            derecha={ojo}
          />

          <Campo
            etiqueta="Contraseña nueva"
            type={ver ? 'text' : 'password'}
            autoComplete="new-password"
            icono={<Candado size={18} />}
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
            error={errores.contrasenaNueva}
          />

          <Campo
            etiqueta="Ingrese nuevamente la contraseña"
            type={ver ? 'text' : 'password'}
            autoComplete="new-password"
            icono={<Candado size={18} />}
            value={repetida}
            onChange={(e) => setRepetida(e.target.value)}
            error={errores.repetida}
          />

          {errorGeneral && (
            <p role="alert" className="text-sm font-medium text-red-600">
              {errorGeneral}
            </p>
          )}

          {listo && (
            <p role="status" className="text-sm font-medium text-emerald-700">
              Listo: tu contraseña cambió y las otras sesiones se cerraron.
            </p>
          )}

          <Boton type="submit" cargando={enviando}>
            {enviando ? 'Cambiando…' : 'Cambiar la contraseña'}
          </Boton>
        </form>
      </section>
    </div>
  );
}
